import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Input, Select, Switch, message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { updateConfiguration } from '../../../redux/Configuration/actions';
import { getAllEvents } from '../../../redux/Event/actions';
import { getAllUsers } from '../../../redux/User/actions';
import { getAllSessions } from '../../../redux/Session/actions';
import EditMachineButton from '../Machines/EditMachineButton';
import { fetchFreeMachines } from '../../../redux/Machine/actions';
import ImageUpload from './ImageUpload';
import PrintSettings from './AddConfiguration/PrintSettings';
import ShareSetting from './AddConfiguration/ShareSetting';
import GameSetting from './AddConfiguration/GameSetting';

const { Option } = Select;

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const EditConfigButton = ({ item }) => {
  const events = useSelector(state => state.events.data);
  const machines = useSelector(state => state.machines.machines);
  const sessions = useSelector(state => state.sessions.data);
  const users = useSelector(state => state.users?.data || []);
  const dispatch = useDispatch();

  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const [selectedMachine, setSelectedMachine] = useState(item.machine?._id || null);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [overlayImages, setOverlayImages] = useState(item.overlayImages || []);
  const [gifOverlays, setGifOverlays] = useState(item.gifOverlays || []);
  const [greenScreenBackgrounds, setGreenScreenBackgrounds] = useState(item.greenScreenBackgrounds || []);
  const [printSettings, setPrintSettings] = useState({});
  const [smsConfig, setSmsConfig] = useState({ SMS: item.SMS });
  const [facebookConfig, setFacebookConfig] = useState({
    appId: item.appId || '',
    pageId: item.pageId || '',
    appSecret: item.appSecret || '',
    facebookToken: item.facebookToken || '',
  });
  const [game, setGame] = useState(item.game || false);
  const [winners, setWinners] = useState(item.winners || []);

  useEffect(() => {
    if (visible) {
      dispatch(getAllEvents());
      dispatch(fetchFreeMachines());
      dispatch(getAllUsers());
      dispatch(getAllSessions());
    }
  }, [visible, dispatch]);

  useEffect(() => {
    if (item.event?._id) {
      const filtered = sessions.filter(session => session.event?._id === item.event._id);
      setFilteredSessions(filtered);
    }
  }, [sessions, item.event]);

  const showModal = () => setVisible(true);

  const hideModal_Ok = () => form.submit();

  const hideModal_Cancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedMachine(null);
    setFilteredSessions([]);
    setOverlayImages([]);
    setGifOverlays([]);
    setGreenScreenBackgrounds([]);
    setPrintSettings({});
    setSmsConfig({ SMS: false });
    setFacebookConfig({
      appId: '',
      pageId: '',
      appSecret: '',
      facebookToken: '',
    });
    setGame(false);
    setWinners([]);
  };

  const handleMachineChange = (machineId) => {
    setSelectedMachine(machineId);
  };

  const handleEventChange = (eventId) => {
    const filtered = sessions.filter(session => session.event?._id === eventId);
    setFilteredSessions(filtered);
  };

  const form_onFinish = async (values) => {
    const updatedConfig = {
      ...values,
      overlayImages,
      gifOverlays,
      greenScreenBackgrounds,
      ...printSettings,
      ...smsConfig,
      ...facebookConfig,
      game,
      winners: game && winners.length > 0 ? winners.join(', ') : winners,
    };

    await dispatch(updateConfiguration(item._id, updatedConfig));
    message.success('Configuration updated successfully!');
    setVisible(false);
  };

  return (
    <>
      <Button type="link" onClick={showModal}>
        Edit Configuration
      </Button>
      <Modal
        title="Edit Configuration"
        visible={visible}
        onOk={hideModal_Ok}
        onCancel={hideModal_Cancel}
        okText="Update"
        cancelText="Cancel"
      >
        <Form
          {...layout}
          form={form}
          onFinish={form_onFinish}
          initialValues={{
            confName: item.confName || '',
            event: item.event?._id || '',
            machine: item.machine?._id || '',
            user: item.user?._id || '',
            isActive: item.isActive,
            setting: item.setting,
            theme: item.theme,
            SMS: item.SMS,
            printable: item.printable,
            autoprint: item.autoprint,
            photoRatio: item.photoRatio,
            sendGif: item.sendGif,
          }}
        >
          <Form.Item label="Configuration Name" name="confName" rules={[{ required: true, message: 'Please input the configuration name!' }]}>
            <Input />
          </Form.Item>

          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item label="Event" name="event" rules={[{ required: true, message: 'Please select an event!' }]}>
            <Select onChange={handleEventChange}>
              {events.map(event => (
                <Option key={event._id} value={event._id}>
                  {event.eventname}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Machine" name="machine" rules={[{ required: true, message: 'Please select a machine!' }]}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Select
                onChange={handleMachineChange}
                value={selectedMachine}
                style={{ flex: 1, marginRight: 8 }}
              >
                {machines.map(machine => (
                  <Option key={machine._id} value={machine._id}>
                    {machine.machinename}
                  </Option>
                ))}
              </Select>
              {selectedMachine && (
                <EditMachineButton
                  item={machines.find(machine => machine._id === selectedMachine)}
                  users={users}
                />
              )}
            </div>
          </Form.Item>

          <Form.Item label="Session" name="session" rules={[{ required: true, message: 'Please select a session!' }]}>
            <Select>
              {filteredSessions.map(session => (
                <Option key={session._id} value={session._id}>
                  {session.sessionname}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="User" name="user" rules={[{ required: true, message: 'Please select a user!' }]}>
            <Select>
              {users.map(user => (
                <Option key={user._id} value={user._id}>
                  {user.username}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Other Components */}
          <Form.Item label="Overlay Image" name="overlayImage">
        <ImageUpload
          setImageUrl={(url) => setOverlayImages([...overlayImages, url])}
        />
        {overlayImages.length > 0 &&
          overlayImages.map((url, index) => (
            <img
              key={index}
              src={url}
              alt="Overlay"
              style={{ marginTop: 10, width: "100%" }}
            />
          ))}
      </Form.Item>

      <Form.Item label="GIF Overlay" name="gifOverlay">
        <ImageUpload
          setImageUrl={(url) => setGifOverlays([...gifOverlays, url])}
        />
        {gifOverlays.length > 0 &&
          gifOverlays.map((url, index) => (
            <img
              key={index}
              src={url}
              alt="GIF Overlay"
              style={{ marginTop: 10, width: "100%" }}
            />
          ))}
      </Form.Item>

      <Form.Item label="Green Screen Background" name="greenScreenBackground">
        <ImageUpload
          setImageUrl={(url) =>
            setGreenScreenBackgrounds([...greenScreenBackgrounds, url])
          }
        />
        {greenScreenBackgrounds.length > 0 &&
          greenScreenBackgrounds.map((url, index) => (
            <img
              key={index}
              src={url}
              alt="Green Screen Background"
              style={{ marginTop: 10, width: "100%" }}
            />
          ))}
      </Form.Item>
          <PrintSettings printSettings={printSettings} onChange={setPrintSettings} />
          <ShareSetting smsConfig={smsConfig} setSmsConfig={setSmsConfig} facebookConfig={facebookConfig} setFacebookConfig={setFacebookConfig} />
          <GameSetting game={game} setGame={setGame} winners={winners} onWinnersChange={setWinners} />

        </Form>
      </Modal>
    </>
  );
};

export default EditConfigButton;
